import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Mail, AlertCircle, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (res.ok) {
        setSuccess(true);
      } else {
        const data = await res.json();
        setError(data.detail || 'Something went wrong');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 pt-20">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1a237e] rounded-full mb-4">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h1 data-testid="forgot-password-title" className="text-2xl font-bold text-[#0d1442]">{t('auth.forgotTitle')}</h1>
              <p className="text-slate-500 mt-2">{t('auth.forgotSubtitle')}</p>
            </div>

            {success ? (
              <div data-testid="forgot-password-success" className="text-center">
                <div className="flex items-center justify-center gap-2 p-4 mb-6 bg-green-50 border border-green-200 rounded-lg text-green-700">
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{t('auth.resetSent')}</span>
                </div>
                <Link
                  data-testid="back-to-login-link"
                  to="/login"
                  className="text-[#1a237e] font-medium hover:underline text-sm"
                >
                  {t('auth.backToLogin')}
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="forgot-password-form">
                {error && (
                  <div data-testid="forgot-password-error" className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div className="mb-6">
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    {t('auth.email')}
                  </label>
                  <input
                    data-testid="forgot-password-email-input"
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a237e] focus:border-transparent"
                    required
                    autoFocus
                  />
                </div>

                <button
                  data-testid="send-reset-link-button"
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-[#1a237e] text-white font-medium rounded-lg hover:bg-[#0d1442] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? '...' : t('auth.sendResetLink')}
                </button>
              </form>
            )}

            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-slate-500 hover:text-[#1a237e] transition-colors">
                {t('auth.backToLogin')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPasswordPage;
